import { useState, useEffect } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/layout/PageHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Phone, Search, ShoppingCart, ArrowRightLeft, Server, Loader2, Check, Globe, Building2, Info, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import ComplianceUpload from '../components/ComplianceUpload';

export default function PhoneSetup() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState('');
  const [activeTab, setActiveTab] = useState('purchase'); // purchase, forwarding, sip
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('IT');
  const [searchLocality, setSearchLocality] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [clinicPhone, setClinicPhone] = useState(null);
  const [forwardingNumber, setForwardingNumber] = useState('');
  const [sipInfo, setSipInfo] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [countryCompliance, setCountryCompliance] = useState(null);

  useEffect(() => {
    loadClinics();
    loadCountries();
    loadWallet();
  }, []);

  useEffect(() => {
    if (selectedClinic) loadClinicPhone();
  }, [selectedClinic]);

  const loadClinics = async () => {
    try {
      const res = await api.get('/clinics');
      setClinics(Array.isArray(res.data) ? res.data : res.data.clinics || []);
    } catch (err) { console.error('Errore caricamento cliniche:', err); }
  };

  const loadCountries = async () => {
    try {
      const res = await api.get('/phone-numbers/countries');
      setCountries(res.data.countries || []);
    } catch (err) { console.error('Errore caricamento paesi:', err); }
  };

  const loadClinicPhone = async () => {
    try {
      const res = await api.get(`/phone-numbers/clinic/${selectedClinic}`);
      setClinicPhone(res.data);
    } catch (err) { console.error('Errore caricamento numero clinica:', err); setClinicPhone(null); }
  };

  const loadWallet = async () => {
    try {
      const res = await api.get('/billing/wallet');
      setWalletBalance(res.data.balance);
    } catch (err) { console.error('Errore caricamento wallet:', err); }
  };

  const handleSearch = async () => {
    setSearching(true);
    try {
      const params = new URLSearchParams({ country_code: selectedCountry, limit: '20' });
      if (searchLocality) params.append('locality', searchLocality);
      const res = await api.get(`/phone-numbers/search?${params}`);
      setSearchResults(res.data.numbers || []);
      if (res.data.count === 0) toast.info('Nessun numero trovato. Prova un\'altra città o paese.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore ricerca');
    } finally { setSearching(false); }
  };

  const handlePurchase = async (phoneNumber, monthlyCost) => {
    if (!selectedClinic) { toast.error('Seleziona prima una clinica'); return; }
    if (!window.confirm(`Confermi l'acquisto del numero ${phoneNumber}?\nIl costo verrà addebitato dal tuo wallet.`)) return;
    setPurchasing(true);
    try {
      const res = await api.post('/phone-numbers/purchase', { phone_number: phoneNumber, clinic_id: selectedClinic, monthly_cost: monthlyCost || 1.00 });
      toast.success(res.data.message);
      if (res.data.new_balance !== undefined) setWalletBalance(res.data.new_balance);
      setSearchResults([]);
      loadClinicPhone();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Errore acquisto';
      if (err.response?.status === 402) {
        toast.error(`Saldo insufficiente! ${detail}`);
      } else {
        toast.error(detail);
      }
    } finally { setPurchasing(false); }
  };

  const handleForwarding = async () => {
    if (!selectedClinic || !forwardingNumber) { toast.error('Compila tutti i campi'); return; }
    try {
      const res = await api.post('/phone-numbers/forwarding', { clinic_id: selectedClinic, partner_number: forwardingNumber });
      toast.success('Configurazione salvata');
      setClinicPhone(prev => ({ ...prev, forwarding_number: forwardingNumber }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    }
  };

  const loadSipInfo = async () => {
    if (!selectedClinic) return;
    try {
      const res = await api.get(`/phone-numbers/sip-info/${selectedClinic}`);
      setSipInfo(res.data);
    } catch (err) { console.error('Errore caricamento SIP info:', err); }
  };

  const tabs = [
    { id: 'purchase', label: 'Acquista Numero', icon: ShoppingCart },
    { id: 'forwarding', label: 'Porta il tuo Numero', icon: ArrowRightLeft },
    { id: 'sip', label: 'SIP Trunk', icon: Server },
  ];

  return (
    <Layout>
      <PageHeader title="Numeri Telefonici" subtitle="Configura i numeri TelcaVoIP per le tue cliniche" />
      <div className="content-area max-w-4xl" data-testid="phone-setup-page">

        {/* Clinic Selector */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <div className="flex items-center gap-3 mb-3">
            <Building2 className="w-5 h-5 text-sky-500" />
            <Label className="font-semibold">Seleziona Clinica</Label>
          </div>
          <Select value={selectedClinic} onValueChange={setSelectedClinic}>
            <SelectTrigger data-testid="clinic-select"><SelectValue placeholder="Scegli una clinica..." /></SelectTrigger>
            <SelectContent>
              {clinics.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {clinicPhone?.configured && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">Numero attivo: <strong>{clinicPhone.phone_number}</strong></span>
            </div>
          )}
        </div>

        {selectedClinic && (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => { setActiveTab(tab.id); if (tab.id === 'sip') loadSipInfo(); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition ${
                    activeTab === tab.id ? 'bg-sky-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-sky-300'
                  }`} data-testid={`tab-${tab.id}`}>
                  <tab.icon className="w-4 h-4" /> {tab.label}
                </button>
              ))}
            </div>

            {/* Tab A: Purchase */}
            {activeTab === 'purchase' && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h3 className="font-semibold mb-1">Acquista un numero TelcaVoIP</h3>
                    <p className="text-sm text-slate-500">Cerca e attiva un nuovo numero dedicato per la tua clinica.</p>
                  </div>
                  {walletBalance !== null && (
                    <div className="text-right shrink-0 ml-4" data-testid="wallet-balance">
                      <p className="text-xs text-slate-500">Saldo Wallet</p>
                      <p className={`text-lg font-bold ${walletBalance > 5 ? 'text-emerald-600' : 'text-red-500'}`}>€{walletBalance.toFixed(2)}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="w-40">
                    <Label className="text-xs">Paese</Label>
                    <Select value={selectedCountry} onValueChange={(v) => { setSelectedCountry(v); setCountryCompliance(null); setSearchResults([]); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {countries.map(c => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name} ({c.prefix})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[150px]">
                    <Label className="text-xs">Citta (opzionale)</Label>
                    <Input value={searchLocality} onChange={e => setSearchLocality(e.target.value)} placeholder="Es. Roma, Milano..." data-testid="locality-input" />
                  </div>
                  <div className="self-end">
                    <Button onClick={handleSearch} disabled={searching || (!isSuperadmin && countryCompliance && !countryCompliance.approved)} className="bg-sky-500 hover:bg-sky-600" data-testid="search-numbers-btn">
                      {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                      Cerca Numeri
                    </Button>
                  </div>
                </div>

                {/* Compliance check for selected country (skip for superadmin) */}
                {!isSuperadmin && (
                  <div className="mb-4">
                    <ComplianceUpload
                      countryCode={selectedCountry}
                      countryName={countries.find(c => c.code === selectedCountry)?.name || selectedCountry}
                      onStatusChange={setCountryCompliance}
                    />
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 text-xs font-medium text-slate-500 flex">
                      <span className="flex-1">Numero</span>
                      <span className="w-32">Localita</span>
                      <span className="w-24 text-right">Costo/mese</span>
                      <span className="w-24"></span>
                    </div>
                    {searchResults.map((num, i) => (
                      <div key={i} className="px-4 py-3 border-t border-slate-100 flex items-center hover:bg-slate-50" data-testid={`number-${i}`}>
                        <span className="flex-1 font-mono text-sm font-medium">{num.phone_number}</span>
                        <span className="w-32 text-sm text-slate-500">{num.locality || num.region || '-'}</span>
                        <span className="w-24 text-sm text-right text-slate-600">€{num.monthly_cost}</span>
                        <span className="w-24 text-right">
                          <Button size="sm" onClick={() => handlePurchase(num.phone_number, num.monthly_cost)} disabled={purchasing}
                            className="bg-sky-500 hover:bg-sky-600 text-xs" data-testid={`buy-${i}`}>
                            {purchasing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Attiva'}
                          </Button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab B: Forwarding */}
            {activeTab === 'forwarding' && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="font-semibold mb-1">Porta il tuo numero esistente</h3>
                <p className="text-sm text-slate-500 mb-5">
                  Mantieni il tuo numero attuale e inoltra le chiamate verso il numero TelcaVoIP della clinica.
                  I pazienti continueranno a chiamare il numero che gia conoscono.
                </p>

                {clinicPhone?.phone_number ? (
                  <>
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-sky-700"><strong>Numero TelcaVoIP della clinica:</strong> {clinicPhone.phone_number}</p>
                    </div>
                    <div className="mb-4">
                      <Label>Il tuo numero attuale</Label>
                      <Input value={forwardingNumber} onChange={e => setForwardingNumber(e.target.value)} placeholder="+39 02 1234567" data-testid="forwarding-input" />
                    </div>
                    <Button onClick={handleForwarding} className="bg-sky-500 hover:bg-sky-600 mb-6" data-testid="save-forwarding-btn">
                      <Check className="w-4 h-4 mr-2" /> Salva Configurazione
                    </Button>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-sky-500" /> Istruzioni</h4>
                      <ol className="text-sm text-slate-600 space-y-2 list-decimal pl-5">
                        <li>Contatta il tuo operatore telefonico (fisso o mobile)</li>
                        <li>Richiedi l'attivazione del <strong>trasferimento di chiamata incondizionato</strong></li>
                        <li>Indica come numero di destinazione: <strong>{clinicPhone.phone_number}</strong></li>
                        <li>Una volta attivo, tutte le chiamate saranno gestite dall'assistente AI</li>
                      </ol>
                    </div>
                  </>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-700">La clinica non ha ancora un numero TelcaVoIP assegnato. Acquista prima un numero dalla scheda "Acquista Numero".</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab C: SIP Trunk */}
            {activeTab === 'sip' && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="font-semibold mb-1">Connessione SIP Trunk</h3>
                <p className="text-sm text-slate-500 mb-5">
                  Collega il tuo centralino VoIP direttamente alla rete TelcaVoIP per un'integrazione professionale.
                </p>

                {sipInfo ? (
                  <div className="space-y-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Server SIP:</span>
                        <span className="font-mono font-medium">{sipInfo.sip_server}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Porta:</span>
                        <span className="font-mono">{sipInfo.sip_port}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Trasporto:</span>
                        <span className="font-mono">{sipInfo.transport}</span>
                      </div>
                      {sipInfo.telcavoip_number && (
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Numero TelcaVoIP:</span>
                          <span className="font-mono font-medium">{sipInfo.telcavoip_number}</span>
                        </div>
                      )}
                    </div>
                    <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                      <p className="text-sm text-sky-700">{sipInfo.instructions}</p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">Caricamento informazioni SIP...</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
